import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import { toast } from "react-toastify";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import "../../../css/pages/SalesFunnel/CardModal.css";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { resolveUploadedFileUrl } from "../../../helpers/resolveMediaUrl";

function CardModal({ modalOpen, onClose, selectedCard, contact, reloadBoard, srcCover }) {
	const [card, setCard] = useState(selectedCard);
	const [users, setUsers] = useState([]);
	const [allTags, setAllTags] = useState([]);
	const [selectedTags, setSelectedTags] = useState([]);
	const [tagInputValue, setTagInputValue] = useState("");
	const [tagSearchDebounced, setTagSearchDebounced] = useState("");
	const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
	const tagDropdownRef = useRef(null);

	const { user } = useContext(AuthContext);
	const userId = user?.profile !== "admin" ? user?.id : null;

	useEffect(() => {
		if (!tagDropdownOpen) return;
		const t = setTimeout(() => setTagSearchDebounced(tagInputValue), 180);
		return () => clearTimeout(t);
	}, [tagInputValue, tagDropdownOpen]);

	useEffect(() => {
		if (!modalOpen) return;
		const handleClickOutside = (e) => {
			if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target)) {
				setTagDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [modalOpen]);

	useEffect(() => {
		if (!modalOpen) return;
		const prevOverflow = document.body.style.overflow;
		const prevPaddingRight = document.body.style.paddingRight;
		const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
		document.body.style.overflow = "hidden";
		if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;
		return () => {
			document.body.style.overflow = prevOverflow;
			document.body.style.paddingRight = prevPaddingRight;
		};
	}, [modalOpen]);

	const [loading, setLoading] = useState(false);
	const [image, setImage] = useState(srcCover);
	const [attachments, setAttachments] = useState([]);

	useEffect(() => {
		if (selectedCard) setCard(selectedCard);
	}, [selectedCard]);

	useEffect(() => {
		if (!modalOpen) return;
		let list = [];
		try {
			const raw = card?.attachments ?? selectedCard?.attachments;
			if (typeof raw === "string" && raw) list = JSON.parse(raw);
			else if (Array.isArray(raw)) list = [...raw];
		} catch (_) {}
		setAttachments(list);
	}, [modalOpen, card?.attachments, selectedCard?.attachments]);

	useEffect(() => {
		if (contact?.tags) setSelectedTags(Array.isArray(contact.tags) ? [...contact.tags] : []);
	}, [contact?.id, contact?.tags, modalOpen]);

	useEffect(() => {
		if (!modalOpen) {
			setTagInputValue("");
			setTagSearchDebounced("");
		}
	}, [modalOpen]);

	useEffect(() => {
		if (!modalOpen) return;
		api.get("/users")
			.then(({ data: { users: u } }) => setUsers(u))
			.catch((err) => console.log(err));
		api.get("/tags/list")
			.then(({ data }) => setAllTags(Array.isArray(data) ? data : []))
			.catch((err) => console.log(err));
	}, [modalOpen]);

	useEffect(() => {
		setImage(srcCover);
	}, [srcCover]);

	const handleAddAttachment = async (e) => {
		if (!e.target.files?.length) return;
		const file = e.target.files[0];
		e.target.value = "";
		try {
			const formData = new FormData();
			formData.append("file", file);
			const response = await api.post("/bulk-messaging/upload-image", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			const url = resolveUploadedFileUrl(file, response);
			setAttachments((prev) => [...prev, url]);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRemoveAttachment = (index) => {
		setAttachments((prev) => prev.filter((_, i) => i !== index));
	};

	const isImageUrl = (url) => /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(url || "");
	const getAttachmentLabel = (url) => {
		try {
			const path = url.split("/").pop() || "";
			return decodeURIComponent(path.split("?")[0]) || "Arquivo";
		} catch (_) {
			return "Arquivo";
		}
	};

	const syncTags = async (tags) => {
		if (!contact?.id) return;
		try {
			await api.post("/tags/sync", { contactId: contact.id, tags });
			queueMicrotask(reloadBoard);
		} catch (err) {
			toastError(err);
		}
	};

	const createTag = async (name) => {
		try {
			const { data } = await api.post("/tags", {
				name,
				color: "#475569",
			});
			return data;
		} catch (err) {
			toastError(err);
			return null;
		}
	};

	const addTag = async (tagOrName, isCreate = false) => {
		let tagToAdd = isCreate ? await createTag(typeof tagOrName === "string" ? tagOrName.trim() : tagOrName?.name?.trim()) : tagOrName;
		if (isCreate && tagToAdd) {
			const fresh = await api.get("/tags/list").then((r) => r.data);
			setAllTags(Array.isArray(fresh) ? fresh : []);
		}
		if (!tagToAdd || selectedTags.some((t) => t.id === tagToAdd.id)) return;
		const next = [...selectedTags, tagToAdd];
		setSelectedTags(next);
		await syncTags(next);
		setTagInputValue("");
		setTagSearchDebounced("");
		setTagDropdownOpen(false);
	};

	const handleTagSelect = (tag) => {
		addTag(tag, false);
	};

	const handleTagCreate = () => {
		const name = tagInputValue?.trim();
		if (!name) return;
		addTag(name, true);
	};

	const handleRemoveTag = async (tagToRemove) => {
		const next = selectedTags.filter((t) => t.id !== tagToRemove.id);
		setSelectedTags(next);
		await syncTags(next);
	};

	const availableTags = useMemo(
		() => allTags.filter((t) => !selectedTags.some((s) => s.id === t.id)),
		[allTags, selectedTags]
	);

	const filteredTags = useMemo(() => {
		const searchLower = (tagSearchDebounced || "").trim().toLowerCase();
		return searchLower
			? availableTags.filter((t) => t.name?.toLowerCase().includes(searchLower))
			: availableTags;
	}, [tagSearchDebounced, availableTags]);

	const showCreateOption = useMemo(() => {
		const searchLower = (tagInputValue || "").trim().toLowerCase();
		if (searchLower.length === 0) return false;
		const exactMatch = availableTags.some((t) => t.name?.toLowerCase() === searchLower);
		return !exactMatch;
	}, [tagInputValue, availableTags]);

	const handleClose = () => onClose();

	const handleSaveCard = async () => {
		const funnelContact = selectedCard ?? contact?.FunnelContact ?? contact?.FunnelContact;
		const contactId = contact?.id ?? funnelContact?.contactId;
		const salesFunnelColumnId = contact?.salesFunnelColumnId;
		if (!contactId || !salesFunnelColumnId) {
			toast.error("Dados do cartão incompletos.");
			return;
		}
		setLoading(true);
		try {
			await api.post("/sales-funnel/pin-contact", {
				contactId,
				salesFunnelColumnId,
				description: card?.description ?? undefined,
				cover: card?.cover ?? undefined,
				attachments: attachments.length ? JSON.stringify(attachments) : undefined,
				userId: card?.userId ?? undefined,
				expiresIn: card?.expiresIn ?? undefined,
				valor: card?.valor ?? undefined,
				requestUserId: userId
			});
			toast.success("Cartão salvo com sucesso.");
			reloadBoard();
			handleClose();
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const handleUpdateMedia = async (e) => {
		if (!e.target.files?.length) return;
		const img = e.target.files[0];
		e.preventDefault();
		const formData = new FormData();
		formData.append("file", img);
		const response = await api.post("/bulk-messaging/upload-image", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
		const url = resolveUploadedFileUrl(img, response);
		setCard((prev) => ({ ...prev, cover: url }));
		setImage(url);
	};

	if (!modalOpen) return null;

	return (
		<div className="sf-card-modal-overlay" role="presentation">
			<div className="sf-card-modal" role="dialog" aria-labelledby="sf-card-modal-title">
				<header className="sf-card-modal-header">
					<h2 id="sf-card-modal-title" className="sf-card-modal-title">Editar cartão</h2>
					<button
						type="button"
						className="sf-card-modal-close"
						onClick={handleClose}
						aria-label="Fechar"
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
					</button>
				</header>

				<section className="sf-card-modal-section">
					<h3 className="sf-card-modal-section-title">Informações</h3>
					<div className="sf-card-modal-content">
						<div className="sf-card-modal-field-group">
							<label className="sf-card-modal-label">Descrição</label>
							<textarea
								className="sf-card-modal-field"
								rows={4}
								placeholder="Descrição do cartão"
								value={card?.description ?? ""}
								onChange={(e) => setCard({ ...card, description: e.target.value })}
							/>
						</div>
						<div className="sf-card-modal-field-group sf-card-modal-field-group--third">
							<label className="sf-card-modal-label">Responsável</label>
							<select
								className="sf-card-modal-field"
								value={card?.userId ?? ""}
								onChange={(e) => setCard({ ...card, userId: e.target.value ? Number(e.target.value) : null })}
							>
								<option value="">Selecione</option>
								{users.map((user) => (
									<option key={user.id} value={user.id}>{user.name}</option>
								))}
							</select>
						</div>
						<div className="sf-card-modal-field-group sf-card-modal-field-group--third">
							<label className="sf-card-modal-label">Data limite</label>
							<input
								type="date"
								className="sf-card-modal-field"
								value={card?.expiresIn?.split("T")[0] ?? ""}
								onChange={(e) =>
									setCard({ ...card, expiresIn: `${e.target.value}T04:00:00.000Z` })
								}
							/>
						</div>
						<div className="sf-card-modal-field-group sf-card-modal-field-group--third">
							<label className="sf-card-modal-label">Valor (R$)</label>
							<input
								type="text"
								className="sf-card-modal-field"
								placeholder="0,00"
								value={card?.valor ?? ""}
								onChange={(e) => setCard({ ...card, valor: e.target.value })}
							/>
						</div>
					</div>
				</section>

				{contact?.id && (
					<section className="sf-card-modal-section">
						<h3 className="sf-card-modal-section-title">Etiquetas</h3>
						<div className="sf-card-modal-tags">
							<div className="sf-card-modal-tags-list">
								{selectedTags.map((tag) => (
									<span
										key={tag.id}
										className="sf-card-modal-tag"
										style={{ backgroundColor: tag.color || "#64748b" }}
										title={tag.name}
									>
										<span className="sf-card-modal-tag-label">{tag.name}</span>
										<button
											type="button"
											className="sf-card-modal-tag-remove"
											onClick={() => handleRemoveTag(tag)}
											aria-label={`Remover ${tag.name}`}
										>
											<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
										</button>
									</span>
								))}
							</div>
							<div className="sf-card-modal-tags-add sf-card-modal-tags-add--with-dropdown relative" ref={tagDropdownRef}>
								<input
									type="text"
									className="sf-card-modal-field"
									placeholder="Digite para buscar ou cadastrar etiqueta"
									value={tagInputValue}
									onChange={(e) => setTagInputValue(e.target.value)}
									onFocus={() => setTagDropdownOpen(true)}
									onKeyDown={(e) => {
										if (e.key === "Enter" && showCreateOption) {
											e.preventDefault();
											handleTagCreate();
										}
									}}
									aria-label="Adicionar etiqueta"
									aria-controls="sf-card-modal-tag-listbox"
									aria-haspopup="listbox"
								/>
								{tagDropdownOpen && (filteredTags.length > 0 || showCreateOption) && (
									<ul
										id="sf-card-modal-tag-listbox"
										className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
										role="listbox"
									>
										{filteredTags.map((tag) => (
											<li
												key={tag.id}
												role="option"
												aria-selected={false}
												className="cursor-pointer px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
												onClick={() => handleTagSelect(tag)}
											>
												{tag.name}
											</li>
										))}
										{showCreateOption && (
											<li
												role="option"
												aria-selected={false}
												className="cursor-pointer border-t border-slate-100 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
												onClick={handleTagCreate}
											>
												Cadastrar &quot;{tagInputValue.trim()}&quot;
											</li>
										)}
									</ul>
								)}
							</div>
						</div>
					</section>
				)}

				<section className="sf-card-modal-section">
					<h3 className="sf-card-modal-section-title">Capa</h3>
					{image ? (
						<div className="sf-card-modal-image-wrap">
							<img src={image} width={400} height={200} alt="Capa" style={{ objectFit: "cover" }} />
							<button
								type="button"
								className="sf-card-modal-btn-remover"
								onClick={() => {
									setImage("");
									setCard((prev) => ({ ...prev, cover: null }));
								}}
							>
								Remover capa
							</button>
						</div>
					) : (
						<label className="sf-card-modal-attach" title="Anexar imagem">
							<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/></svg>
							Anexar imagem
							<input
								accept="image/png, image/jpeg, image/jpg"
								type="file"
								hidden
								onChange={handleUpdateMedia}
							/>
						</label>
					)}
				</section>

				<section className="sf-card-modal-section">
					<h3 className="sf-card-modal-section-title">Anexos</h3>
					<div className="sf-card-modal-attachments">
						{attachments.length > 0 && (
							<div className="sf-card-modal-attachments-list">
								{attachments.map((url, index) => (
									<div key={`${url}-${index}`} className="sf-card-modal-attachment-item">
										{isImageUrl(url) ? (
											<img src={url} alt="" className="sf-card-modal-attachment-thumb" />
										) : (
											<a href={url} target="_blank" rel="noopener noreferrer" className="sf-card-modal-attachment-file">
												<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
												<span className="sf-card-modal-attachment-filename" title={getAttachmentLabel(url)}>{getAttachmentLabel(url)}</span>
											</a>
										)}
										<button
											type="button"
											className="sf-card-modal-attachment-remove"
											onClick={() => handleRemoveAttachment(index)}
											aria-label="Remover anexo"
										>
											<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
										</button>
									</div>
								))}
							</div>
						)}
						<label className="sf-card-modal-attach sf-card-modal-attach-small" title="Anexar arquivo (imagem, PDF, documento, planilha, etc.)">
							<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
							Anexar mais
							<input
								type="file"
								hidden
								onChange={handleAddAttachment}
							/>
						</label>
					</div>
				</section>

				<div className="sf-card-modal-actions">
					<button
						type="button"
						className="sf-card-modal-btn"
						onClick={handleClose}
					>
						Cancelar
					</button>
					<button
						type="button"
						className="sf-card-modal-btn sf-card-modal-btn-primary"
						disabled={loading}
						onClick={handleSaveCard}
					>
						{loading && <span className="sf-card-modal-spinner" />}
						Salvar
					</button>
				</div>
			</div>
		</div>
	);
}

export default CardModal;
